import * as React from 'react'

import { IResourceRecord } from '~/client/src/desktop/components/Resource/types'
import { useTableFilters } from '~/client/src/desktop/components/TableFilters'
import { TableFiltersProps } from '~/client/src/desktop/components/TableFilters/types'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import { UNASSIGNED_FILTER_VALUE } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import { expandAllWhenBookable } from './expandAllWhenBookable'
import {
  PERMITTED_COMPANIES_KEY,
  SHOW_IN_MAP_KEY,
  WORKFLOW_RESOURCE_KEY,
} from './resourceControlsColumns'

/** Facet keys owned by the control fields (vs the resource's data properties). */
export const CONTROL_FACET_KEYS = new Set<string>([
  PERMITTED_COMPANIES_KEY,
  WORKFLOW_RESOURCE_KEY,
  SHOW_IN_MAP_KEY,
])

/** The boolean control facets, labelled Yes/No rather than by option name. */
const BOOLEAN_FACET_KEYS = new Set<string>([
  WORKFLOW_RESOURCE_KEY,
  SHOW_IN_MAP_KEY,
])

interface IParams {
  records: IResourceRecord[]
  getCompanyById: (id: string) => Company | undefined
  /** All company option ids — used to expand "All" (empty) values (R9). */
  allCompanyIds: string[]
  /** The controls this resource exposes — gates the Show in Maps facet. */
  controls: readonly string[]
}

interface IControlsFilters {
  filterProps: TableFiltersProps
  applyFilters: (records: IResourceRecord[]) => IResourceRecord[]
}

/**
 * Filter facets for the **control** fields (Permitted Companies, WR). The resource's data-property facets come from the provider (`meta`); the
 * Controls toolbar merges the two facet sets (see ResourceControlsContent).
 */
export function useResourceControlsFilters({
  records,
  getCompanyById,
  allCompanyIds,
  controls,
}: IParams): IControlsFilters {
  const getOptionName = React.useCallback(
    (optionId: string, facetKey?: string) => {
      if (optionId === UNASSIGNED_FILTER_VALUE) {
        return Localization.translator.unassigned
      }
      if (facetKey && BOOLEAN_FACET_KEYS.has(facetKey)) {
        return optionId === 'true'
          ? Localization.translator.yes
          : Localization.translator.no
      }
      return getCompanyById(optionId)?.name ?? optionId
    },
    [getCompanyById],
  )

  const filterColumns = React.useMemo(
    () => [
      // Independent of the WR gate; defaults to shown when the record has no
      // stored value, so it facets as "Yes" rather than "Unassigned".
      ...(controls.includes(SHOW_IN_MAP_KEY)
        ? [
            {
              dataKey: SHOW_IN_MAP_KEY,
              label: Localization.translator.showInMap,
              accessorFn: (record: IResourceRecord) =>
                record.root?.showInMap ?? true,
              isFacetable: true,
              isSearchable: false,
            },
          ]
        : []),
      {
        dataKey: PERMITTED_COMPANIES_KEY,
        label: Localization.translator.permittedCompanies,
        accessorFn: (record: IResourceRecord) =>
          expandAllWhenBookable(
            record.root?.permittedCompanies ?? [],
            record.root?.isBookable ?? false,
            allCompanyIds,
          ),
        facetDisplayFormatter: getOptionName,
        isFacetable: true,
        isSearchable: false,
      },
      {
        dataKey: WORKFLOW_RESOURCE_KEY,
        label: Localization.translator.workflowResource,
        accessorFn: (record: IResourceRecord) =>
          record.root?.isBookable ?? false,
        isFacetable: true,
        isSearchable: false,
      },
    ],
    [getOptionName, allCompanyIds, controls],
  )

  const { filterProps, applyFilters } = useTableFilters<IResourceRecord>({
    records,
    columns: filterColumns,
  })

  const controlsFilterProps = React.useMemo<TableFiltersProps>(
    () => ({ ...filterProps, getOptionName }),
    [filterProps, getOptionName],
  )

  return { filterProps: controlsFilterProps, applyFilters }
}
